import React, { useEffect, useState } from 'react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import FlashBanner from '@/components/ui/flash-banner';
import { Loader2 } from 'lucide-react';

type LoginFormProps = Omit<React.ComponentProps<'div'>, 'onError'> & {
  onEmailLogin?: (email: string, password: string) => Promise<void> | void;
  onSuccess?: () => void;
  onError?: (error: Error) => void;
  isLoading?: boolean;
  title?: string;
  subtitle?: string;
  forgotHref?: string;
  submitLabel?: string;
  flashAlert?: string | null;
  flashNotice?: string | null;
  defaultEmail?: string;
};

export function LoginForm({
  className,
  onEmailLogin,
  onSuccess,
  onError,
  isLoading = false,
  title = 'Bentornato su Emovia',
  subtitle = 'Entra e riprendi da dove avevi lasciato.',
  forgotHref = '/patients/password/new',
  submitLabel = 'Accedi',
  flashAlert,
  flashNotice,
  defaultEmail = '',
  ...props
}: LoginFormProps) {
  const [email, setEmail] = useState(defaultEmail);
  const [password, setPassword] = useState('');

  useEffect(() => {
    setEmail(defaultEmail);
  }, [defaultEmail]);

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!onEmailLogin) return;
    try {
      await onEmailLogin(email, password);
      onSuccess?.();
    } catch (err) {
      onError?.(err instanceof Error ? err : new Error('Login failed'));
    }
  };

  return (
    <div className={cn('flex w-full flex-col gap-6', className)} {...props}>
      <Card className="rounded-3xl border-white/20 bg-white/90 backdrop-blur-xl transition-all duration-300 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.45),0_25px_60px_-15px_rgba(120,64,255,0.25)] supports-[backdrop-filter:none]:bg-white supports-[backdrop-filter:none]:border-slate-200">
        <CardHeader className="space-y-2 text-center text-slate-900">
          <CardTitle className="text-2xl font-semibold tracking-tight md:text-3xl">
            {title}
          </CardTitle>
          <CardDescription className="text-base text-slate-600">{subtitle}</CardDescription>
        </CardHeader>
        <CardContent className="text-left text-slate-900">
          {flashAlert && <FlashBanner message={flashAlert} tone="error" className="mb-4" />}
          {flashNotice && <FlashBanner message={flashNotice} tone="success" className="mb-4" />}
          <form onSubmit={handleEmailSubmit} className="space-y-6">
            <div className="grid gap-3">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="m@example.com"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isLoading}
                className="bg-white/95 text-slate-900 placeholder:text-slate-500 focus:bg-white border-slate-200"
                autoComplete="email"
              />
            </div>
            <div className="grid gap-3">
              <div className="flex items-center">
                <Label htmlFor="password">Password</Label>
                <a
                  href={forgotHref}
                  className="ml-auto text-sm font-medium text-slate-700 underline-offset-4 transition-colors hover:text-slate-900 hover:underline"
                >
                  Password dimenticata?
                </a>
              </div>
              <Input
                id="password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={isLoading}
                className="bg-white/95 text-slate-900 focus:bg-white border-slate-200"
                autoComplete="current-password"
              />
            </div>
            <Button
              type="submit"
              className="bg-primary hover:bg-primary/80 w-full hover:cursor-pointer transition-colors"
              // onMouseEnter={(e) => {
              //   (e.currentTarget as HTMLButtonElement).style.backgroundColor =
              //     'var(--emovia-primary-hover)';
              // }}
              // onMouseLeave={(e) => {
              //   (e.currentTarget as HTMLButtonElement).style.backgroundColor =
              //     'var(--emovia-primary)';
              // }}
              disabled={isLoading}
            >
              {isLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                  Accesso in corso...
                </span>
              ) : (
                submitLabel
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

export default LoginForm;
